import { inject, ref } from 'vue'
import { useAuthStore } from '../stores/AuthStore'
import { PERMISSION_KEYS } from '../access/permissionKeys'

/**
 * Shared access gate for the Transform Learning views. Every Transform view must:
 *   1. wait for the user profile/permissions to resolve (on a full reload onMounted
 *      runs before the profile loads, so hasPermission would wrongly return false), then
 *   2. redirect users without `transform.access` back to Help & Resources (or Home
 *      when they also lack `core.access`), and
 *   3. only render once the check resolves so locked users never see a flash of content.
 *
 * Usage in a view's onMounted:
 *   const { isAccessChecked, ensureAccess } = useTransformAccessGate()
 *   if (!(await ensureAccess())) return
 *   ... load data ...
 *   isAccessChecked.value = true
 *
 * @returns {{ isAccessChecked, ensureAccess }}
 */
export function useTransformAccessGate() {
  const customNavigation = inject('customNavigation')
  const authStore = useAuthStore()

  // Gates rendering until both the access check and the initial data load resolve.
  const isAccessChecked = ref(false)

  // Resolves the access check. Returns true when the user may view the page; on denial
  // it redirects to Help & Resources (core users) or Home and returns false.
  async function ensureAccess() {
    await authStore.ensureAccessLoaded()

    // Check permissions
    if (!authStore.hasPermission(PERMISSION_KEYS.TRANSFORM_ACCESS)) {
      // Help & Resources is itself core-gated; for non-core users navigate() would
      // reject it and leave them on a blank gated view, so fall back to Home.
      const fallbackPage = authStore.hasPermission(PERMISSION_KEYS.CORE_ACCESS)
        ? customNavigation.value.routes.helpAndResources.name
        : customNavigation.value.routes.Home.name
      customNavigation.value.navigate({ newPage: fallbackPage })
      return false
    }
    return true
  }

  return { isAccessChecked, ensureAccess }
}
